/**
 * Background job list poller
 *
 * Periodically fetches GET /api/v1/jobs and diffs against local state.
 * Discovers new/changed/deleted jobs from other clients or tabs.
 */

import type { ApiClient } from "./api-client.js";
import type { JobInfo } from "../types/api.js";

export interface JobSnapshot {
  status: string;
  hasAudio: boolean;
  hasCover: boolean;
  stego: { technique: string; payload_size: number; encoded_at: string } | null;
}

export interface JobsDiff {
  added: JobInfo[];
  updated: JobInfo[];
  removed: string[];
}

export interface JobPollerOptions {
  api: ApiClient;
  getCurrentJobs: () => Map<string, JobSnapshot>;
  onChanged: (diff: JobsDiff, allJobs: JobInfo[]) => void;
}

const INTERVAL_ACTIVE_MS = 15_000;
const INTERVAL_IDLE_MS = 30_000;

export function startJobPoller(options: JobPollerOptions): () => void {
  const { api, getCurrentJobs, onChanged } = options;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  function getInterval(jobs: JobInfo[]): number {
    const hasPending = jobs.some(
      (j) => j.status === "pending" || j.status === "processing",
    );
    return hasPending ? INTERVAL_ACTIVE_MS : INTERVAL_IDLE_MS;
  }

  function diffJobs(incoming: JobInfo[]): JobsDiff | null {
    const current = getCurrentJobs();
    const added: JobInfo[] = [];
    const updated: JobInfo[] = [];
    const removed: string[] = [];

    const incomingIds = new Set<string>();

    for (const job of incoming) {
      incomingIds.add(job.task_id);
      const existing = current.get(job.task_id);
      if (!existing) {
        added.push(job);
      } else if (
        existing.status !== job.status ||
        existing.hasAudio !== job.has_audio ||
        existing.hasCover !== job.has_cover ||
        stegoChanged(existing.stego, job.stego)
      ) {
        updated.push(job);
      }
    }

    for (const taskId of current.keys()) {
      if (!incomingIds.has(taskId)) {
        removed.push(taskId);
      }
    }

    if (added.length === 0 && updated.length === 0 && removed.length === 0) {
      return null;
    }
    return { added, updated, removed };
  }

  function stegoChanged(
    a: JobSnapshot["stego"],
    b: JobInfo["stego"],
  ): boolean {
    if (a === null && b === null) return false;
    if (a === null || b === null) return true;
    return (
      a.technique !== b.technique ||
      a.payload_size !== b.payload_size ||
      a.encoded_at !== b.encoded_at
    );
  }

  async function poll(): Promise<void> {
    if (stopped) return;

    let jobs: JobInfo[] = [];
    try {
      const response = await api.jobs();
      jobs = response.jobs;
    } catch {
      // Silent — background poll failures are expected during transient issues
      scheduleNext([]);
      return;
    }

    if (stopped) return;

    const diff = diffJobs(jobs);
    if (diff) {
      onChanged(diff, jobs);
    }

    scheduleNext(jobs);
  }

  function scheduleNext(jobs: JobInfo[]): void {
    if (stopped) return;
    timer = setTimeout(() => poll(), getInterval(jobs));
  }

  // Page Visibility API — pause when hidden, immediate poll on return
  function handleVisibilityChange(): void {
    if (stopped) return;
    if (document.hidden) {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    } else {
      // Tab became visible — poll immediately
      if (timer !== null) clearTimeout(timer);
      timer = null;
      poll();
    }
  }

  document.addEventListener("visibilitychange", handleVisibilityChange);

  // Start first poll after one interval (the initial job list was just fetched in connect())
  scheduleNext([]);

  return function stop(): void {
    stopped = true;
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  };
}
